import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { addDocument, getDocuments, updateDocument, deleteDocument } from '../services/database';
import { Button } from '../components/common/Button';
import { Input } from '../components/common/Input';
import { Card, CardHeader, CardBody } from '../components/common/Card';
import { Modal } from '../components/common/Modal';
import {
  Mail,
  Phone,
  MapPin,
  Cake,
  Search,
  UserPlus,
  Users,
  Pencil,
  Trash2,
  BadgeCheck
} from 'lucide-react';
import { format, parseISO, differenceInYears } from 'date-fns';
import { es } from 'date-fns/locale';

const PageContainer = styled.div`
  padding: 24px 20px;
  max-width: 1400px;
  margin: 0 auto;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 12px 10px;
  }
`;

const Header = styled.div`
  position: relative;
  padding: 22px 24px;
  margin-bottom: 18px;
  background: linear-gradient(135deg, rgba(46, 91, 136, 0.06) 0%, rgba(79, 179, 191, 0.09) 100%);
  border: 1px solid rgba(79, 179, 191, 0.2);
  border-radius: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  overflow: hidden;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.8) inset,
    0 6px 20px -8px rgba(79, 179, 191, 0.18);

  &::before {
    content: '';
    position: absolute;
    top: -40%;
    right: -10%;
    width: 220px;
    height: 220px;
    background: radial-gradient(circle, rgba(79, 179, 191, 0.22) 0%, transparent 65%);
    pointer-events: none;
  }

  h1 {
    position: relative;
    margin: 0;
    font-size: 2rem;
    font-weight: 800;
    letter-spacing: -1px;
    background: linear-gradient(135deg, #1E40AF 0%, #4FB3BF 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  p {
    position: relative;
    margin: 4px 0 0;
    font-size: 13.5px;
    color: #64748b;
    font-weight: 500;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 16px 14px;
    margin-bottom: 14px;
    border-radius: 16px;
    flex-direction: column;
    align-items: stretch;
    gap: 12px;

    h1 {
      font-size: 1.4rem;
      letter-spacing: -0.5px;
    }
  }
`;

const SearchSection = styled.div`
  position: relative;
  margin-bottom: 18px;

  svg.search-icon {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    width: 18px;
    height: 18px;
    color: #94a3b8;
    pointer-events: none;
    z-index: 1;
  }

  input {
    padding-left: 42px !important;
  }
`;

const CustomerGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 14px;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`;

const CustomerCard = styled.div`
  position: relative;
  background: linear-gradient(180deg, #ffffff 0%, #fbfdfe 100%);
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 18px;
  padding: 18px;
  overflow: hidden;
  isolation: isolate;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.95) inset,
    0 1px 2px rgba(15, 23, 42, 0.04),
    0 6px 20px -8px rgba(46, 91, 136, 0.1);
  transition:
    transform 260ms cubic-bezier(0.22, 1, 0.36, 1),
    box-shadow 260ms ease,
    border-color 260ms ease;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #2E5B88 0%, #4FB3BF 50%, #2E5B88 100%);
    z-index: 1;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 170px;
    height: 170px;
    background: radial-gradient(circle at top right, rgba(79, 179, 191, 0.08) 0%, transparent 60%);
    pointer-events: none;
    z-index: 0;
  }

  & > * {
    position: relative;
    z-index: 2;
  }

  &:hover {
    transform: translateY(-2px);
    border-color: rgba(79, 179, 191, 0.4);
    box-shadow:
      0 1px 0 rgba(255, 255, 255, 0.95) inset,
      0 1px 3px rgba(15, 23, 42, 0.05),
      0 14px 32px -10px rgba(46, 91, 136, 0.18),
      0 4px 10px -2px rgba(79, 179, 191, 0.12);
  }

  &.birthday-soon {
    border-color: rgba(236, 72, 153, 0.45);

    &::before {
      background: linear-gradient(90deg, #F472B6 0%, #EC4899 50%, #F472B6 100%);
    }
    &::after {
      background: radial-gradient(circle at top right, rgba(236, 72, 153, 0.14) 0%, transparent 60%);
    }
  }
`;

const CustomerHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 8px;
  margin-bottom: 14px;
  padding-bottom: 14px;
  border-bottom: 1px dashed rgba(148, 163, 184, 0.3);
`;

const Avatar = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: ${props => props.$birthday
    ? 'linear-gradient(135deg, #F472B6 0%, #EC4899 100%)'
    : 'linear-gradient(135deg, #2E5B88 0%, #4FB3BF 100%)'};
  color: #ffffff;
  font-size: 18px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: ${props => props.$birthday
    ? '0 4px 12px -2px rgba(236, 72, 153, 0.4)'
    : '0 4px 12px -2px rgba(46, 91, 136, 0.4)'};
  letter-spacing: 0.5px;
`;

const CustomerNameBlock = styled.div`
  flex: 1;
  min-width: 0;

  h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    color: #1e293b;
    letter-spacing: -0.3px;
    line-height: 1.2;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .meta {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 4px;
    font-size: 12px;
    color: #64748b;
    font-weight: 500;

    .doc-chip {
      display: inline-flex;
      align-items: center;
      gap: 3px;
      padding: 2px 8px;
      background: rgba(79, 179, 191, 0.1);
      color: #0E7490;
      border: 1px solid rgba(79, 179, 191, 0.25);
      border-radius: 999px;
      font-size: 10.5px;
      font-weight: 700;
      letter-spacing: 0.3px;

      svg { width: 11px; height: 11px; stroke-width: 2.5; }
    }

    .age {
      color: #94a3b8;
    }
  }
`;

const CustomerInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 0;

  .icon {
    width: 28px;
    height: 28px;
    border-radius: 8px;
    background: linear-gradient(135deg, rgba(46, 91, 136, 0.1) 0%, rgba(79, 179, 191, 0.14) 100%);
    color: #2E5B88;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    svg {
      width: 14px;
      height: 14px;
      stroke-width: 2.3;
    }
  }

  .content {
    flex: 1;
    min-width: 0;
  }

  label {
    display: block;
    font-size: 9.5px;
    font-weight: 700;
    color: #94a3b8;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    margin-bottom: 1px;
  }

  span {
    font-size: 13px;
    font-weight: 600;
    color: #1e293b;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: block;
    line-height: 1.2;
  }
`;

const BirthdayBadge = styled.span`
  position: absolute;
  top: 14px;
  right: 14px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: linear-gradient(135deg, #F472B6 0%, #EC4899 100%);
  color: #ffffff;
  border-radius: 999px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.25) inset,
    0 3px 10px -2px rgba(236, 72, 153, 0.45);
  z-index: 3;

  svg { width: 12px; height: 12px; stroke-width: 2.5; }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid rgba(148, 163, 184, 0.18);
`;

const EmptyState = styled.div`
  padding: 48px 20px;
  text-align: center;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.96) 0%, rgba(246, 250, 253, 0.92) 100%);
  border: 1px dashed rgba(148, 163, 184, 0.35);
  border-radius: 16px;
  color: #94a3b8;
  font-size: 14px;
  font-weight: 500;
  grid-column: 1 / -1;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${props => props.theme.spacing.md};
`;

export const Customers = () => {
  const { userId } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [formData, setFormData] = useState({
    name: '',
    document: '',
    email: '',
    phone: '',
    address: '',
    birthdate: ''
  });

  useEffect(() => {
    loadCustomers();
  }, [userId]);

  const loadCustomers = async () => {
    if (!userId) return;
    
    try {
      setLoading(true);
      const customersData = await getDocuments(userId, 'customers');
      setCustomers(customersData);
    } catch (error) {
      console.error('Error loading customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const customerData = {
        ...formData,
        birthdate: formData.birthdate ? new Date(formData.birthdate).toISOString() : null
      };

      if (editingCustomer) {
        await updateDocument(userId, 'customers', editingCustomer.id, customerData);
      } else {
        await addDocument(userId, 'customers', customerData);
      }

      await loadCustomers();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving customer:', error);
      alert('Error al guardar el cliente');
    }
  };

  const handleDelete = async (customerId) => {
    if (window.confirm('¿Está seguro de eliminar este cliente?')) {
      try {
        await deleteDocument(userId, 'customers', customerId);
        await loadCustomers();
      } catch (error) {
        console.error('Error deleting customer:', error);
        alert('Error al eliminar el cliente');
      }
    }
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      name: customer.fullName || customer.name || '',
      document: customer.document || '',
      email: customer.email || '',
      phone: customer.phone || '',
      address: customer.address || '',
      birthdate: customer.birthdate ? format(parseISO(customer.birthdate), 'yyyy-MM-dd') : ''
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingCustomer(null);
    setFormData({
      name: '',
      document: '',
      email: '',
      phone: '',
      address: '',
      birthdate: ''
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const isUpcomingBirthday = (birthdate) => {
    if (!birthdate) return false;
    
    const today = new Date();
    const birthday = parseISO(birthdate);
    const thisYearBirthday = new Date(today.getFullYear(), birthday.getMonth(), birthday.getDate());
    const daysUntilBirthday = Math.ceil((thisYearBirthday - today) / (1000 * 60 * 60 * 24));
    
    return daysUntilBirthday >= 0 && daysUntilBirthday <= 7;
  };

  const calculateAge = (birthdate) => {
    if (!birthdate) return null;
    return differenceInYears(new Date(), parseISO(birthdate));
  };

  const filteredCustomers = customers.filter(customer => {
    const searchLower = searchTerm.toLowerCase();
    const customerName = customer.fullName || customer.name || '';
    const customerDocument = customer.document || '';
    
    return customerName.toLowerCase().includes(searchLower) ||
           customerDocument.toLowerCase().includes(searchLower) ||
           (customer.email && customer.email.toLowerCase().includes(searchLower)) ||
           (customer.phone && customer.phone.includes(searchTerm));
  });

  return (
    <PageContainer>
      <Header>
        <div>
          <h1>Gestión de Clientes</h1>
          <p>Administra la información de tus clientes y sus cumpleaños</p>
        </div>
        <Button onClick={() => setShowModal(true)}>
          <UserPlus size={16} style={{ marginRight: 6 }} />
          Agregar Cliente
        </Button>
      </Header>

      <SearchSection>
        <Search className="search-icon" />
        <Input
          type="text"
          placeholder="Buscar por nombre, email o teléfono..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ marginBottom: 0 }}
        />
      </SearchSection>

      {loading ? (
        <EmptyState>Cargando clientes...</EmptyState>
      ) : (
        <CustomerGrid>
          {filteredCustomers.map(customer => {
            const name = customer.fullName || customer.name || 'Sin nombre';
            const initials = name.trim().split(/\s+/).slice(0, 2).map(p => p[0]).join('').toUpperCase() || 'C';
            const birthday = isUpcomingBirthday(customer.birthdate);

            return (
              <CustomerCard
                key={customer.id}
                className={birthday ? 'birthday-soon' : ''}
              >
                {birthday && (
                  <BirthdayBadge>
                    <Cake />
                    Cumpleaños
                  </BirthdayBadge>
                )}

                <CustomerHeader>
                  <Avatar $birthday={birthday}>{initials}</Avatar>
                  <CustomerNameBlock>
                    <h3>{name}</h3>
                    <div className="meta">
                      {customer.document && (
                        <span className="doc-chip">
                          <BadgeCheck />
                          CC {customer.document}
                        </span>
                      )}
                      {customer.birthdate && (
                        <span className="age">{calculateAge(customer.birthdate)} años</span>
                      )}
                    </div>
                  </CustomerNameBlock>
                </CustomerHeader>

                {customer.email && (
                  <CustomerInfo>
                    <div className="icon"><Mail /></div>
                    <div className="content">
                      <label>Correo</label>
                      <span>{customer.email}</span>
                    </div>
                  </CustomerInfo>
                )}

                {customer.phone && (
                  <CustomerInfo>
                    <div className="icon"><Phone /></div>
                    <div className="content">
                      <label>Teléfono</label>
                      <span>{customer.phone}</span>
                    </div>
                  </CustomerInfo>
                )}

                {customer.address && (
                  <CustomerInfo>
                    <div className="icon"><MapPin /></div>
                    <div className="content">
                      <label>Dirección</label>
                      <span>{customer.address}</span>
                    </div>
                  </CustomerInfo>
                )}

                {customer.birthdate && (
                  <CustomerInfo>
                    <div className="icon"><Cake /></div>
                    <div className="content">
                      <label>Cumpleaños</label>
                      <span>{format(parseISO(customer.birthdate), 'dd/MM/yyyy', { locale: es })}</span>
                    </div>
                  </CustomerInfo>
                )}

                <ActionButtons>
                  <Button size="small" onClick={() => handleEdit(customer)}>
                    <Pencil size={14} style={{ marginRight: 4 }} />
                    Editar
                  </Button>
                  <Button size="small" variant="danger" onClick={() => handleDelete(customer.id)}>
                    <Trash2 size={14} style={{ marginRight: 4 }} />
                    Eliminar
                  </Button>
                </ActionButtons>
              </CustomerCard>
            );
          })}

          {filteredCustomers.length === 0 && (
            <EmptyState>
              <Users size={32} style={{ display: 'block', margin: '0 auto 12px', opacity: 0.5 }} />
              No se encontraron clientes
            </EmptyState>
          )}
        </CustomerGrid>
      )}

      <Modal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingCustomer ? 'Editar Cliente' : 'Agregar Cliente'}
        size="medium"
      >
        <form onSubmit={handleSubmit}>
          <FormGrid>
            <Input
              label="Nombre Completo"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
            />
            
            <Input
              label="Cédula/Documento (CC)"
              name="document"
              value={formData.document}
              onChange={handleInputChange}
              required
            />
            
            <Input
              label="Correo Electrónico"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
            />
            
            <Input
              label="Teléfono"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleInputChange}
            />
            
            <Input
              label="Fecha de Cumpleaños"
              name="birthdate"
              type="date"
              value={formData.birthdate}
              onChange={handleInputChange}
            />
          </FormGrid>
          
          <Input
            label="Dirección"
            name="address"
            value={formData.address}
            onChange={handleInputChange}
          />
          
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '24px' }}>
            <Button type="button" variant="outline" onClick={handleCloseModal}>
              Cancelar
            </Button>
            <Button type="submit">
              {editingCustomer ? 'Actualizar' : 'Guardar'}
            </Button>
          </div>
        </form>
      </Modal>
    </PageContainer>
  );
};